import { Link, Stack, Typography } from "@mui/material";
import { useCallback, useLayoutEffect, useState } from "react";
import { SOLUTION_TYPE_NAME_MAP } from "..";
import HelpDialogLayout, {
  BodyTypographies,
  ContentStack,
  HelpDialogLayoutProps,
  SectionStack,
} from "./HelpDialogLayout";

export interface AwakeningDialogProps
  extends Omit<HelpDialogLayoutProps, "title"> {
  prematureMorningAwakening?: boolean;
}
export default function AwakeningDialog({
  prematureMorningAwakening,
  open,
  ...props
}: AwakeningDialogProps) {
  const [awakeningElement, setAwakeningElement] =
    useState<HTMLSpanElement | null>(null);
  const [stressElement, setStressElement] = useState<HTMLSpanElement | null>(
    null
  );
  const [
    prematureMorningAwakeningElement,
    setPrematureMorningAwakeningElement,
  ] = useState<HTMLSpanElement | null>(null);

  const scrollTo = useCallback(
    (
      type: "awakening" | "premature_morning_awakening" | "stress",
      opt: ScrollIntoViewOptions = { behavior: "smooth" }
    ) => {
      const elm = {
        awakening: awakeningElement,
        premature_morning_awakening: prematureMorningAwakeningElement,
        stress: stressElement,
      }[type];
      elm?.scrollIntoView(opt);
    },
    [awakeningElement, prematureMorningAwakeningElement, stressElement]
  );

  const handleWASOClick = useCallback(() => {
    scrollTo("awakening");
  }, [scrollTo]);

  const handleStressClick = useCallback(() => {
    scrollTo("stress");
  }, [scrollTo]);
  useLayoutEffect(() => {
    if (prematureMorningAwakening) {
      // 初期表示時、早く目が覚めてしまうことが気になる場合へスクロール
      scrollTo("premature_morning_awakening", { behavior: "auto" });
    }
  }, [prematureMorningAwakening, scrollTo]);
  return (
    <HelpDialogLayout
      {...{ open, ...props }}
      title={`${SOLUTION_TYPE_NAME_MAP.awakening}`}
    >
      <Stack gap={12}>
        <SectionStack>
          <Typography
            ref={setAwakeningElement}
            variant="subtitle2"
            color="#3D3D3D"
          >
            中途覚醒が気になる場合
          </Typography>
          <ContentStack>
            <BodyTypographies
              contents={[
                "中途覚醒は自覚がないものも多く、客観的な測定で一晩に1時間未満であれば正常の範囲となります。それを超える場合は、睡眠の質が悪い可能性があります。",
                "中途覚醒は生活習慣に大きく影響される他、個人差はありますが加齢や就寝環境、ストレスによっても左右されます。",
              ]}
            />
            <BodyTypographies
              contents={[
                "年齢と共に睡眠を維持する機構が弱くなっていくため、歳を重ねると中途覚醒の時間が増えやすくなります。",
                "若い頃は一度も起きずに朝まで眠れていたのに、と思うかもしれませんが、中途覚醒自体が増えているので自覚がある中途覚醒が増えることは自然な現象です。",
                "寝ている間に目が覚めても、そのまますぐ眠れるのであれば気にしすぎる必要はありません。",
              ]}
            />
            <BodyTypographies
              contents={[
                "就寝環境にも大きな影響を受けます。枕やマットレスなどの寝具、ペットを含むベッドパートナー、布団内の温度、周囲の騒音などが挙げられます。これらに心当たりがある場合は、環境を見直すことで中途覚醒が改善する可能性があります。",
              ]}
            />
          </ContentStack>
        </SectionStack>
        <SectionStack>
          <Typography
            ref={setStressElement}
            variant="subtitle2"
            color="#3D3D3D"
          >
            ストレスと睡眠の関係
          </Typography>
          <ContentStack>
            <BodyTypographies
              contents={[
                "ストレスは、一般的に「不安や悩み」といったネガティブなものが想起されますが、興奮や期待といったポジティブなストレスも睡眠に影響を与えます。",
                "ストレスを受けると交感神経が優位になり、本来であれば睡眠時に働く副交感神経が抑制されることで、睡眠が妨げられます。",
              ]}
            />
            <BodyTypographies
              contents={[
                "例えば、遠足の前日に眠れないような一時的なストレスは、その要因が解消されれば自然に改善するため、あまり気にする必要はありません。",
                "しかし、仕事や人間関係などのストレスが続く場合は、睡眠を妨げてしまうため、就寝前にストレスを軽減することが重要です。",
                "例えば、ナイトルーチンとして、入浴、ストレッチやヨガ、音楽やアロマなど自分がリラックスできるものを取り入れてみましょう。",
                "また、考え事や不安を紙に書き出すことも有効です。「書く」行為はシンプルながら、感情の浄化作用が強く、また感情を客観視したり新しい視点でとらえる機会ともなり、不安が改善し、ストレスが大幅に軽減することが心理学や社会学にて報告されています。",
                "日中の適度な運動もストレス耐性を高める効果的な手段です。覚醒を減らす効果もあるため、活用してみてください。",
              ]}
            />
          </ContentStack>
        </SectionStack>
        <SectionStack>
          <Typography
            ref={setPrematureMorningAwakeningElement}
            variant="subtitle2"
            color="#3D3D3D"
          >
            早く目が覚めてしまうことが気になる場合
          </Typography>
          <ContentStack>
            <BodyTypographies
              contents={[
                "早く目が覚めてしまう、には2種類あります。ひとつは「自分が望む起床時刻よりも早く目が覚め、その後眠れない」場合、もうひとつは「明け方に目が覚めてしまうが、その後二度寝できる」場合です。",
                <>
                  ここで言う「早く目が覚めてしまう」は前者を指しており、後者については二度寝できているので
                  <Link
                    color="#3D3D3D"
                    onClick={handleWASOClick}
                    sx={{ cursor: "pointer", fontSize: "14px" }}
                  >
                    中途覚醒が気になる場合
                  </Link>
                  を参照してください。
                </>,
              ]}
            />
            <BodyTypographies
              contents={[
                "早く目が覚めてしまってその後眠れない場合、生活習慣以外に、個人差はありますが加齢や就寝環境、ストレスの影響も受けます。",
                "加齢に伴い、人間は睡眠時間が徐々に短くなっていくため、年を取ってから長く寝ようとしても難しくなります。例えば、夜の21時に寝て、夜中の3時に目が覚めてその後眠れない場合、まだ寝たかったと思うかもしれませんが、65歳頃であれば6時間の睡眠は適正な長さです。",
                "また、歳を重ねると、自然に朝型になり早寝早起きとなっていくのが一般的な変化です。",
              ]}
            />
            <BodyTypographies
              contents={[
                "就寝環境にも大きな影響を受けます。枕やマットレスなどの寝具、ペットを含むベッドパートナー、布団内の温度、周囲の騒音などが挙げられます。これらに心当たりがある場合は、環境を見直すことで早すぎる目覚めが改善する可能性があります。",
              ]}
            />
            <BodyTypographies
              contents={[
                <>
                  ストレスについては
                  <Link
                    color="#3D3D3D"
                    onClick={handleStressClick}
                    sx={{ cursor: "pointer", fontSize: "14px" }}
                  >
                    ストレスと睡眠の関係
                  </Link>
                  を参照してください。
                </>,
              ]}
            />
          </ContentStack>
        </SectionStack>
      </Stack>
    </HelpDialogLayout>
  );
}
